import { SwitchCase } from '@toss/react';
import type { WebsiteData } from '@/api/ai/website/types';
import type { YoutubeVideoData } from '@/api/ai/youtube/types';
import { cn } from '@/utils';
import { PromptTextArea } from '@/views/build/features/ai-prompt-input/components/PromptTextArea';
import { useAttachment } from './AttachmentProvider';
export interface PromptInputProps {
  /** 프롬프트 텍스트 */
  prompt: string;
  /** 프롬프트 텍스트 변경 핸들러 */
  onChangePrompt: (value: string) => void;
  /** native maxLength — 초과 입력 hard-block (locale별 한도) */
  maxLength?: number;
}

/**
 * 프롬프트 입력 UI 컴포넌트 -- AttachmentProvider context 기반
 *
 * 책임:
 * - PromptTextArea.TextArea 렌더링
 * - Attachment type별 버튼 렌더링 (SwitchCase)
 * - isDragActive 시 버튼 영역 숨김
 * - generating/regenerating 시 버튼 비활성화
 *
 * 반드시 `AttachmentProvider` 내부에서 사용해야 합니다.
 * 모든 첨부 관련 값(openFilePicker, isDragActive, attachment 등)은
 * useAttachment() Context에서 가져옵니다.
 *
 * @example
 * ```tsx
 * <AttachmentProvider
 *   attachment={attachment.state}
 *   onAttachFile={attachment.handleAttachFile}
 *   onAttachYoutube={attachment.handleAttachYoutube}
 *   onDetach={attachment.handleDetach}
 *   generateStatus={generation.status}
 * >
 *   <PromptInput
 *     prompt={form.textPrompt}
 *     onChangePrompt={form.onChangePrompt}
 *   />
 * </AttachmentProvider>
 * ```
 */
export const PromptInput = ({
  prompt,
  onChangePrompt,
  maxLength
}: PromptInputProps) => {
  // Context에서 모든 첨부 관련 값 가져옴 (AttachmentProvider 필수)
  const {
    attachment,
    openFilePicker,
    handleAttachYoutube,
    handleAttachWebsite,
    handleDetach,
    isDragActive,
    generateStatus
  } = useAttachment();

  // generating/regenerating 시 attachment 관련 버튼 비활성화
  const attachmentClickDisabled = generateStatus === 'generating' || generateStatus === 'regenerating';
  return <>
      <PromptTextArea.TextArea className="text-heading-sm-bold" value={prompt} onChange={e => onChangePrompt(e.target.value)} attachmentType={attachment.type} maxLength={maxLength} data-sentry-element="unknown" data-sentry-source-file="PromptInput.tsx" />
      {/* Attachment buttons -- isDragActive 시 숨김 (기존 동작 보존) */}
      <div className={cn('relative flex gap-xs', isDragActive && 'hidden')}>
        <SwitchCase value={attachment.type} caseBy={{
        file: <PromptTextArea.FileAttachmentButton disabled={attachmentClickDisabled} file={attachment.data as File} onDelete={handleDetach} onClick={openFilePicker} />,
        youtube: <PromptTextArea.YoutubeAttachmentButton disabled={attachmentClickDisabled} videoData={attachment.data as YoutubeVideoData} onDelete={handleDetach} onClick={handleAttachYoutube} />,
        website: <PromptTextArea.WebsiteAttachmentButton disabled={attachmentClickDisabled} websiteData={attachment.data as WebsiteData} onDelete={handleDetach} onClick={handleAttachWebsite} />,
        none: <PromptTextArea.MaterialAddButtons onFileClick={openFilePicker} onYoutubeClick={handleAttachYoutube} onWebsiteClick={handleAttachWebsite} disabled={attachmentClickDisabled} />
      }} data-sentry-element="SwitchCase" data-sentry-source-file="PromptInput.tsx" />
      </div>
    </>;
};